import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { MagnifyingGlass, Package, Truck, CheckCircle, Clock, MapPin, WhatsappLogo, CurrencyInr } from '@phosphor-icons/react';
import { trackOrder } from '../utils/api';
import { toast } from 'sonner';
import SEO from '../components/SEO';

const getStatusIcon = (status, completed) => {
  if (status.includes('Order Placed') || status.includes('Order Confirmed')) {
    return <Package size={20} className={completed ? 'text-primary' : 'text-gray-300'} />;
  }
  if (status.includes('Payment')) {
    return <CurrencyInr size={20} className={completed ? 'text-primary' : 'text-gray-300'} />;
  }
  if (status.includes('Processing') || status.includes('Ready') || status.includes('Dispatch')) {
    return <Truck size={20} className={completed ? 'text-primary' : 'text-gray-300'} />;
  }
  if (status.includes('Hold')) {
    return <Clock size={20} className="text-orange-500" />;
  }
  return <CheckCircle size={20} className={completed ? 'text-green-500' : 'text-gray-300'} />;
};

const TrackOrder = () => {
  const [orderId, setOrderId] = useState('');
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleTrack = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      const response = await trackOrder(orderId.trim());
      if (response.data.success && response.data.data) {
        setOrder(response.data.data);
        setError('');
      } else {
        setError(response.data.message || 'Order not found. Please check your order ID.');
        setOrder(null);
      }
    } catch (err) {
      console.error('Track order error:', err);
      setError('Order not found. Please check your order ID or phone number.');
      setOrder(null);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    }).format(price || 0);
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'paid':
        return 'bg-green-100 text-green-700';
      case 'partial':
        return 'bg-blue-100 text-blue-700';
      case 'pending':
        return 'bg-yellow-100 text-yellow-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="min-h-screen bg-background-default">
      <SEO 
        title="Track Order"
        description="Track your Luster Adorn order status. Enter your order ID or phone number to see delivery updates."
        url="/track-order"
      />
      
      {/* Hero */}
      <section className="bg-background-subtle py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Package size={48} className="text-primary mx-auto mb-4" />
            <h1 className="font-display text-4xl text-secondary mb-4">Track Your Order</h1>
            <p className="font-body text-text-secondary max-w-lg mx-auto">
              Enter your invoice number or phone number to track the status of your order
            </p>
          </motion.div>
        </div>
      </section>

      {/* Search Form */}
      <section className="py-12">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <form onSubmit={handleTrack} className="flex gap-4">
            <div className="relative flex-1">
              <MagnifyingGlass size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-text-muted" />
              <input
                type="text"
                value={orderId}
                onChange={(e) => setOrderId(e.target.value)}
                placeholder="Invoice No. (e.g., INV-000001) or Phone"
                required
                data-testid="order-id-input"
                className="w-full pl-12 pr-4 py-4 border border-border-default focus:border-primary focus:outline-none font-body"
              />
            </div>
            <button 
              type="submit" 
              disabled={loading} 
              data-testid="track-btn"
              className="btn-primary px-8 disabled:opacity-50"
            >
              {loading ? 'Tracking...' : 'Track'}
            </button>
          </form>

          {error && (
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="mt-4 text-center text-red-500 font-body"
            >
              {error}
            </motion.p>
          )}
        </div>
      </section>

      {/* Order Details */}
      {order && (
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="pb-16"
        >
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-white p-6 md:p-8 shadow-sm">
              {/* Order Header */}
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8 pb-6 border-b border-border-default">
                <div>
                  <h2 className="font-display text-2xl text-secondary" data-testid="order-number">
                    {order.invoice_number}
                  </h2>
                  <p className="font-body text-sm text-text-muted">
                    Ordered on {order.invoice_date ? new Date(order.invoice_date).toLocaleDateString('en-IN', { 
                      day: 'numeric', month: 'long', year: 'numeric' 
                    }) : 'N/A'}
                  </p>
                </div>
                <span className={`font-body text-sm px-4 py-2 rounded-full capitalize ${getStatusBadgeClass(order.status)}`}>
                  {order.status === 'partial' ? 'Partial Payment' : order.status}
                </span>
              </div>

              {/* Customer Info */}
              <div className="mb-8">
                <h3 className="font-display text-lg text-secondary mb-3">Customer Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="font-body text-sm text-text-muted">Name</p>
                    <p className="font-body text-secondary">{order.customer_name}</p>
                  </div>
                  {order.customer_phone && (
                    <div>
                      <p className="font-body text-sm text-text-muted">Phone</p>
                      <p className="font-body text-secondary">{order.customer_phone}</p>
                    </div>
                  )}
                  {order.customer_city && (
                    <div>
                      <p className="font-body text-sm text-text-muted">City</p>
                      <p className="font-body text-secondary">{order.customer_city} {order.customer_pincode}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Timeline */}
              {order.timeline && order.timeline.length > 0 && (
                <div className="mb-8">
                  <h3 className="font-display text-lg text-secondary mb-6">Order Timeline</h3>
                  <div className="relative">
                    {order.timeline.map((step, index) => (
                      <div key={index} className="flex gap-4 mb-6 last:mb-0">
                        <div className="relative">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            step.completed ? 'bg-primary/10' : 'bg-gray-100'
                          }`}>
                            {getStatusIcon(step.status, step.completed)}
                          </div>
                          {index < order.timeline.length - 1 && (
                            <div className={`absolute left-1/2 top-10 w-0.5 h-8 -translate-x-1/2 ${
                              step.completed ? 'bg-primary' : 'bg-gray-200'
                            }`} />
                          )}
                        </div>
                        <div className="flex-1 pt-1">
                          <p className={`font-body font-medium ${step.completed ? 'text-secondary' : 'text-text-muted'}`}>
                            {step.status}
                          </p>
                          {step.description && (
                            <p className="font-body text-sm text-text-muted">{step.description}</p>
                          )}
                          {step.date && (
                            <p className="font-body text-xs text-text-muted mt-1">{step.date}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Order Items */}
              <div className="mb-8">
                <h3 className="font-display text-lg text-secondary mb-4">Order Items</h3>
                <div className="space-y-3">
                  {order.items?.map((item, index) => (
                    <div key={index} className="flex justify-between items-center py-3 border-b border-border-default last:border-0">
                      <div>
                        <p className="font-body text-secondary">{item.item_name}</p>
                        <p className="font-body text-sm text-text-muted">Qty: {item.quantity} × {formatPrice(item.unit_price)}</p>
                      </div>
                      <p className="font-body text-secondary">{formatPrice(item.total_price)}</p>
                    </div>
                  ))}
                </div>
                
                {/* Totals */}
                <div className="mt-4 pt-4 border-t border-border-default space-y-2">
                  <div className="flex justify-between font-body text-sm">
                    <span className="text-text-muted">Subtotal</span>
                    <span>{formatPrice(order.subtotal)}</span>
                  </div>
                  {order.tax_amount > 0 && (
                    <div className="flex justify-between font-body text-sm">
                      <span className="text-text-muted">Tax</span>
                      <span>{formatPrice(order.tax_amount)}</span>
                    </div>
                  )}
                  {order.discount_amount > 0 && (
                    <div className="flex justify-between font-body text-sm">
                      <span className="text-green-600">Discount</span>
                      <span className="text-green-600">-{formatPrice(order.discount_amount)}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-display text-lg pt-2 border-t border-border-default">
                    <span className="text-secondary">Total</span>
                    <span className="text-primary">{formatPrice(order.total_amount)}</span>
                  </div>
                  <div className="flex justify-between font-body text-sm">
                    <span className="text-text-muted">Paid</span>
                    <span className="text-green-600">{formatPrice(order.paid_amount)}</span>
                  </div>
                  {order.balance_amount > 0 && (
                    <div className="flex justify-between font-body text-sm">
                      <span className="text-orange-600">Balance Due</span>
                      <span className="text-orange-600">{formatPrice(order.balance_amount)}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Payment History */}
              {order.payments && order.payments.length > 0 && (
                <div className="mb-8">
                  <h3 className="font-display text-lg text-secondary mb-4">Payment History</h3>
                  <div className="space-y-2">
                    {order.payments.map((payment, index) => (
                      <div key={index} className="flex justify-between items-center p-3 bg-green-50 rounded">
                        <div>
                          <p className="font-body text-sm text-green-700">
                            {payment.payment_method?.toUpperCase()} Payment
                          </p>
                          <p className="font-body text-xs text-green-600">
                            {payment.payment_date ? new Date(payment.payment_date).toLocaleDateString('en-IN') : ''}
                            {payment.reference_number && ` • Ref: ${payment.reference_number}`}
                          </p>
                        </div>
                        <p className="font-body font-medium text-green-700">{formatPrice(payment.amount)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Help Section */}
              <div className="p-4 bg-background-subtle rounded text-center">
                <p className="font-body text-sm text-text-secondary mb-3">Need help with your order?</p>
                <a
                  href={`https://wa.me/${process.env.REACT_APP_WHATSAPP_NUMBER || '919421147643'}?text=Hi! I need help with order ${order.invoice_number}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="whatsapp-btn inline-flex"
                >
                  <WhatsappLogo size={20} weight="fill" />
                  <span>Contact Support</span>
                </a>
              </div>
            </div>
          </div>
        </motion.section>
      )}
    </div>
  );
};

export default TrackOrder;
